import { FC, useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FarmContext } from "../../../AppContent";
import { Dialog } from "../../../Elements/Dialog";
import { createEvidenceFromFile, getEvidences } from "../../../repos/FileRepo";
import { IEvidence, IEvidenceFilters } from "../../../types/IFile";
import { FilesDropzone } from "../../Files/Dropzone/FilesDropzone";
import { EvidencesList } from "../../Files/EvidencesList";
import { deleteEmptyProps } from "@inceptionbg/ui-components";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  landUuid: string;
  approvalUuid?: string;
  reloadLands?: (callback?: () => void) => void;
  onConfirm?: (evidences: IEvidence[], hasUploadedEvidence: boolean) => void;
  onEvidenceUploaded?: () => void;
  // membersLand?: boolean;
  // usageBasis?: string;
  complementApproval?: boolean;
}

export const EditLandEvidenceDialog: FC<Props> = ({
  isOpen,
  onClose,
  landUuid,
  approvalUuid,
  reloadLands,
  onConfirm,
  onEvidenceUploaded,
  // membersLand,
  // usageBasis,
  complementApproval,
}) => {
  const [evidences, setEvidences] = useState<IEvidence[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState<IEvidenceFilters>({});
  const [hasUploadedEvidence, setHasUploadedEvidence] = useState(false);

  const farmUuid = useContext(FarmContext).farm?.uuid;
  const { t } = useTranslation();

  useEffect(() => {
    setFilters({ land: { uuid: landUuid } });
  }, [landUuid]);

  const handleGetEvidences = useCallback(() => {
    if (farmUuid) {
      setIsLoading(true);
      getEvidences(farmUuid, "LAND", { landUuid, approvalUuid })
        .then((e) => setEvidences(e.evidences))
        .catch(() => {})
        .finally(() => setIsLoading(false));
    }
  }, [farmUuid, landUuid, approvalUuid]);

  useEffect(handleGetEvidences, [handleGetEvidences]);

  useEffect(() => {
    if (isOpen) {
      setHasUploadedEvidence(false);
    }
  }, [isOpen]);

  const onSubmit = (data: File[]) => {
    if (farmUuid) {
      setIsLoading(true);
      setHasUploadedEvidence(true);
      data.forEach((file, i) => {
        createEvidenceFromFile(
          file,
          farmUuid,
          "LAND",
          deleteEmptyProps({
            land: { uuid: landUuid },
            approval: approvalUuid ? { uuid: approvalUuid } : undefined,
            complementApproval: complementApproval,
          })
        )
          .catch(() => {})
          .finally(() => {
            if (i === data.length - 1) {
              setIsLoading(false);
              handleGetEvidences();
              if (onEvidenceUploaded) {
                onEvidenceUploaded();
              }
              // onClose();
            }
          });
      });
    }
  };

  // const isMemberOwnership =
  //   membersLand &&
  //   (usageBasis === "LEASE" ||
  //     usageBasis === "LEASE_LEGAL_ENTITY" ||
  //     usageBasis === "LEASE_CONSOLIDATION" ||
  //     usageBasis === "LEASE_LEGAL_ENTITY_CONSOLIDATION");

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={t("LandEvidenceTitle")}
      desc={t("LandEvidenceDesc")}
      /*descEl={
        isMemberOwnership ? (
          <>
            <p>{t("LandEvidenceTitleMemberOwnership")}</p>
            <a
              href="https://erpg.eagrar.gov.rs/upload/Prilog-2.pdf"
              download
              target="_blank"
              rel="noreferrer"
              className="link"
            >
              {t("PrintStatementForm")}
            </a>
          </>
        ) : undefined
      }*/
      confirmButton={{
        label: t("Confirm"),
        onClick: () => {
          if (onConfirm) {
            onConfirm(evidences, hasUploadedEvidence);
          }
          if (reloadLands) {
            reloadLands();
          }
        },
        disabled: !evidences.length,
      }}
      cancelButton={{}}
      isLoading={isLoading}
      size="m"
    >
      {!!evidences.length && (
        <EvidencesList
          evidences={evidences}
          reloadEvidences={handleGetEvidences}
        />
      )}
      <FilesDropzone
        onSubmit={onSubmit}
        category="LAND"
        reloadEvidences={handleGetEvidences}
        filters={filters}
      />
    </Dialog>
  );
};
